import {useForm} from 'antd/es/form/Form';
import {
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react';

import {IModalOpenerComponentRef} from 'components/modal-opener-component';
import {ITableActionProps} from 'components/table/action';
import {EMetaActionType} from 'entities/metadata';
import {publishListDocuments, updateDocumentsStatus} from 'modules/data/data-actions';
import {TEntityValue} from 'modules/data/data-types';
import {
    useLazyGetAppPropertyQuery,
} from 'store/api/app-properties/app-properties.api';
import {useAppDispatch} from 'store/config/hooks';

import {stepIndex, EStepTitle} from '../document-status-change-modal.constants';
import {IDocumentStatusChangeForm} from '../document-status-change-modal.types';
import {getDocumentComment, sanitizeCommentValue} from '../document-status-change-modal.utils';
import {useFinalStatus} from './use-final-status';
import {useLoadPublishFormMetadata} from './use-load-publish-form-metadata';

/**
 * Интерфейс для параметров хука useDocumentStatusChangeModal
 * @interface
 * @extends {ITableActionProps}
 * @property {Record<string, EntityValue>[]} selectedDocuments - Массив выбранных документов
 */
interface IUseDocumentStatusChangeModalArgs extends ITableActionProps {
    selectedDocuments: Record<string, TEntityValue>[];
}

/**
 * Хук для управления модальным окном изменения статуса документа
 *
 * @param {IUseDocumentStatusChangeModalArgs} props - Параметры хука
 * @param {string} props.entityName - Название сущности
 * @param {object} props.meta - Метаданные
 * @param {Record<string, TEntityValue>[]} props.selectedDocuments - Выбранные документы
 * @returns {Object} Возвращает объект с данными и функциями для управления модальным окном
 * @returns {React.RefObject<IModalOpenerComponentRef>} modalRef - Ссылка на компонент модального окна
 * @returns {boolean} isOpen - Флаг открытия модального окна
 * @returns {FormInstance<IDocumentStatusChangeForm>} form - Экземпляр формы
 * @returns {Record<string, TEntityValue>} firstSelectedEntity - Первый выбранный элемент
 * @returns {number} currentStep - Текущий шаг
 * @returns {boolean} isStepsEnabled - Флаг активации шагов
 * @returns {string | null} finalStatus - Финальный статус
 * @returns {any} publishFormMetadata - Метаданные формы публикации
 * @returns {Function} handleSave - Обработчик сохранения
 * @returns {Function} setCurrentStep - Функция установки текущего шага
 * @returns {Function} setIsOpen - Функция установки открытия модального окна
 * @returns {Function} handleCloseModal - Обработчик закрытия модального окна
 * @returns {Function} handleSubmit - Обработчик отправки формы
 * @returns {Function} handleOpenModal - Обработчик открытия модального окна
 */
export const useDocumentStatusChangeModal = (
    {entityName, meta, selectedDocuments}: IUseDocumentStatusChangeModalArgs,
) => {
    const dispatch = useAppDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [form] = useForm<IDocumentStatusChangeForm>();
    const modalRef = useRef<IModalOpenerComponentRef>(null);
    const {
        referenceUrl, actionType, modalEntityName, actionCode, requestType,
    } = meta;
    const firstSelectedEntity = useMemo(() => selectedDocuments[0], [selectedDocuments]);
    const [currentStep, setCurrentStep] = useState(stepIndex[EStepTitle.COMMENTS]);

    const isPublishListActionType = actionType === EMetaActionType.PUBLISH_LIST;
    const isStepsEnabled = isPublishListActionType;

    const [triggerCommentFieldQuery, isCommentFieldEnabledData] = useLazyGetAppPropertyQuery();
    const isCommentFieldEnabled = isCommentFieldEnabledData.data === 'Y';

    const {
        publishFormMetadata,
        loadPublishFormMetadata,
    } = useLoadPublishFormMetadata({
        isPublishListActionType,
        modalEntityName,
    });

    const {finalStatus, fetchFinalStatus, setFinalStatus} = useFinalStatus({
        selectedDocuments,
        actionCode,
        entityName,
    });

    const handleCloseModal = useCallback(() => {
        form.resetFields();
        setIsOpen(false);
        setFinalStatus(null);
        setCurrentStep(stepIndex[EStepTitle.COMMENTS]);
    }, [form, setFinalStatus]);

    const handleSubmit = () => {
        if (!referenceUrl) {
            return;
        }

        const values = form.getFieldsValue();
        const {createNews, publishDate, delayed} = values;
        const isMultipleDocuments = selectedDocuments.length > 1;
        const sectionCodeCtx: string = String(selectedDocuments?.[0]?.sectionCode);

        const hasAnyUniqueComment = Object.keys(values).some(
            key => {
                const commentValue = values[key] as string | null;
                return key.startsWith('uniqueComment-') && sanitizeCommentValue(commentValue);
            },
        );

        const requestData = selectedDocuments.map(row => {
            const uniqueCommentValue = values[`uniqueComment-${row.id}`] as string | null;

            return {
                docId: String(row.id),
                comment: getDocumentComment(
                    sanitizeCommentValue(uniqueCommentValue),
                    sanitizeCommentValue(values?.sharedComment),
                    isMultipleDocuments,
                    hasAnyUniqueComment,
                ),
            };
        });

        form.validateFields().then(() => {
            if (isPublishListActionType) {
                if (sectionCodeCtx) {
                    dispatch(
                        publishListDocuments(
                            entityName,
                            referenceUrl,
                            requestData,
                            !!createNews,
                            publishDate!,
                            sectionCodeCtx,
                            delayed,
                        ),
                    );
                }
                return;
            }

            dispatch(
                updateDocumentsStatus(
                    entityName,
                    referenceUrl,
                    requestData,
                    actionCode || '',
                    requestType,
                ),
            );
        }).finally(() => {
            handleCloseModal();
        });
    };

    const handleOpenModal = useCallback(() => {
        fetchFinalStatus();
        loadPublishFormMetadata();
        triggerCommentFieldQuery({propertyCode: 'COMMENT_DOCUMENT_WF'}).then(r => {
            if (r.data !== 'Y' && !isPublishListActionType) {
                handleSubmit();
                return;
            }
            if (isPublishListActionType) {
                setCurrentStep(r.data === 'Y' ? stepIndex[EStepTitle.COMMENTS] : stepIndex[EStepTitle.PUBLISH]);
            }
            setIsOpen(true);
        });
    }, [
        fetchFinalStatus,
        loadPublishFormMetadata,
        isPublishListActionType,
    ]);

    const handleSave = useCallback(() => {
        if (!isStepsEnabled || currentStep === stepIndex[EStepTitle.PUBLISH]) {
            form.submit();
            return;
        }

        if (currentStep === stepIndex[EStepTitle.COMMENTS]) {
            setCurrentStep(stepIndex[EStepTitle.PUBLISH]);
        }
    }, [isStepsEnabled, form, currentStep, setCurrentStep]);

    return {
        modalRef,
        isOpen,
        form,
        firstSelectedEntity,
        currentStep,
        isStepsEnabled,
        isCommentFieldEnabled,
        finalStatus,
        publishFormMetadata,
        handleSave,
        setCurrentStep,
        setIsOpen,
        handleCloseModal,
        handleSubmit,
        handleOpenModal,
    };
};
